/* @flow */
import navigation from 'services/NavigationService';
import { apiService } from 'services/ServiceRegistry';
import { activities } from 'components/Activity/ActivityData.js';

import Form from '../_.extend';

/**
 * Safely read account capabilities (asset/rush) from accountStore
 * Returns booleans indicating whether each mode is enabled.
 */
function readAccountCapabilities(
  accountStore?: any
): { assetEnabled: boolean, rushEnabled: boolean } {
  const details =
    accountStore && accountStore.details && accountStore.details.details
      ? accountStore.details.details
      : {};
  return {
    assetEnabled: Boolean(details.asset_request_form_enabled),
    rushEnabled: Boolean(details.is_rush_asset_request_form_customer),
  };
}

/**
 * Decide initial radio value from location/company details:
 * - Prefer rush type if rush flags are present
 * - Else fallback to asset type
 * - Else 'no-type'
 */
function initialLocationType(vdata: Object): 'customer' | 'delivery' | 'no-type' {
  const ld = vdata && vdata.locationDetails ? vdata.locationDetails : {};
  if (ld.rush_asset_request_location && ld.rush_asset_request_location_type) {
    return ld.rush_asset_request_location_type;
  }
  if (ld.asset_request_location && ld.asset_request_location_type) {
    return ld.asset_request_location_type;
  }
  return 'no-type';
}

/**
 * Decide which MODE to use when saving:
 * - If current record already has rush flags -> keep 'rush'
 * - Else if has asset flags -> 'asset'
 * - Else if account capabilities enable exactly one -> that one
 * - Else null (no flags)
 */
function decideModeToPersist(vdata: Object, accountStore?: any): 'rush' | 'asset' | null {
  const ld = vdata && vdata.locationDetails ? vdata.locationDetails : {};
  if (ld.rush_asset_request_location) return 'rush';
  if (ld.asset_request_location) return 'asset';

  const { assetEnabled, rushEnabled } = readAccountCapabilities(accountStore);
  if (rushEnabled && !assetEnabled) return 'rush';
  if (assetEnabled && !rushEnabled) return 'asset';
  return null;
}

/**
 * Apply mutually-exclusive flags into the payload object.
 * Ensures only the chosen mode (rush/asset) is set; the other is cleared.
 */
function applyExclusiveFlags(
  wire: Object,
  mode: 'rush' | 'asset' | null,
  typeValue: 'customer' | 'delivery' | 'no-type'
) {
  // Clear both groups first
  delete wire.asset_request_location;
  delete wire.asset_request_location_type;
  delete wire.rush_asset_request_location;
  delete wire.rush_asset_request_location_type;

  // If user chose "no-type", we store neither group.
  if (typeValue === 'no-type' || !mode) return;

  if (mode === 'rush') {
    wire.rush_asset_request_location = true;
    wire.rush_asset_request_location_type = typeValue;
  } else if (mode === 'asset') {
    wire.asset_request_location = true;
    wire.asset_request_location_type = typeValue;
  }
}

export const makeFields: Function = (vdata: Object, t: Function, accountStore?: any) => {
  if (vdata.locationDetails === null) {
    vdata.locationDetails = {};
  }

  // Compute initial radio value looking at rush-first, then asset, else 'no-type'
  const radioInitialValue = initialLocationType(vdata);

  return [
    {
      // CHOOSE ACCOUNT
      name: 'chooseAccount',
      label: t('general:trading_account'),
      fields: [
        {
          name: 'tradingAccount',
          label: t('general:actions.choose_trading_account'),
          rules: 'required|string',
          value: vdata && vdata.details ? vdata.details.name : null,
          extra: {
            selectID: vdata && vdata.details ? vdata.accountId : null,
          },
        },
      ],
    },
    {
      name: 'location',
      label: t('general:locations.labels.details'),
      fields: [
        {
          name: 'countryId',
          value: vdata && vdata.locationDetails ? vdata.locationDetails.country_id : null,
          rules: 'string',
        },
        {
          name: 'locationCompany',
          label: t('general:company'),
          rules:
            vdata.titleEnd === 'Company' || vdata.titleEnd === 'company' ? 'required|string' : '',
          placeholder: 'Company name...',
          value:
            vdata.titleEnd === 'Company' || vdata.titleEnd === 'company'
              ? vdata.locationDetails.name
              : vdata && vdata.locationDetails && vdata.locationDetails.company
              ? vdata.locationDetails.company.name
              : null,
          extra: {
            selectID:
              vdata && vdata.locationDetails && vdata.locationDetails.company
                ? vdata.locationDetails.company.id
                : null,
            parentId: vdata && vdata.details ? vdata.details.id : null,
            actionButton: true,
            setSelectedFields: [
              { field: 'locationSite', value: null },
              { field: 'locationBuilding', value: null },
              { field: 'locationRoom', value: null },
              { field: 'locationSystem', value: null },
            ],
          },
        },
        {
          name: 'locationSite',
          label: t('general:locations.labels.site'),
          rules: vdata.titleEnd === 'Site' || vdata.titleEnd === 'site' ? 'required|string' : '',
          value:
            vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.site
              ? vdata.locationDetails.hierarchy.site.name
              : null,
          extra: {
            selectID:
              vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.site
                ? vdata.locationDetails.hierarchy.site.id
                : null,
            parentId: vdata && vdata.locationDetails && vdata.locationDetails.company_id,
            actionButton: true,
            setSelectedFields: [
              { field: 'countryId', value: 'country_id' },
              { field: 'locationBuilding', value: null },
              { field: 'locationRoom', value: null },
              { field: 'locationSystem', value: null },
            ],
            childExtraFilters: [
              {
                type: 'formField',
                formName: 'location',
                formField: 'locationCompany',
                isExtra: true,
                value: 'selectID',
              },
              {
                type: 'formField',
                formName: 'location',
                formField: 'countryId',
                isExtra: false,
                value: 'value',
              },
              { type: 'string', value: '?parent_id=' },
            ],
          },
        },
        {
          name: 'locationBuilding',
          label: t('general:locations.labels.building_transport'),
          rules:
            vdata.titleEnd === 'Building/Transport' || vdata.titleEnd === 'building'
              ? 'required|string'
              : '',
          value:
            vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.building
              ? vdata.locationDetails.hierarchy.building.name
              : null,
          extra: {
            selectID:
              vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.building
                ? vdata.locationDetails.hierarchy.building.id
                : null,
            parentId:
              vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.site
                ? `/${vdata.locationDetails.company_id}/${
                    vdata.locationDetails.country_id
                  }?parent_id=${vdata.locationDetails.hierarchy.site.id}`
                : null,
            actionButton: true,
            setSelectedFields: [
              { field: 'locationRoom', value: null },
              { field: 'locationSystem', value: null },
            ],
            childExtraFilters: [
              {
                type: 'formField',
                formName: 'location',
                formField: 'locationCompany',
                isExtra: true,
                value: 'selectID',
              },
              {
                type: 'formField',
                formName: 'location',
                formField: 'countryId',
                isExtra: false,
                value: 'value',
              },
              { type: 'string', value: '?parent_id=' },
            ],
          },
        },
        {
          name: 'locationRoom',
          label: t('general:locations.labels.lab_room'),
          rules:
            vdata.titleEnd === 'Lab/Room' || vdata.titleEnd === 'room' ? 'required|string' : '',
          value:
            vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.room
              ? vdata.locationDetails.hierarchy.room.name
              : null,
          extra: {
            selectID:
              vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.room
                ? vdata.locationDetails.hierarchy.room.id
                : null,
            parentId:
              vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.building
                ? `/${vdata.locationDetails.company_id}/${
                    vdata.locationDetails.country_id
                  }?parent_id=${vdata.locationDetails.hierarchy.building.id}`
                : null,
            actionButton: true,
            setSelectedFields: [{ field: 'locationSystem', value: null }],
            childExtraFilters: [
              {
                type: 'formField',
                formName: 'location',
                formField: 'locationCompany',
                isExtra: true,
                value: 'selectID',
              },
              {
                type: 'formField',
                formName: 'location',
                formField: 'countryId',
                isExtra: false,
                value: 'value',
              },
              { type: 'string', value: '?parent_id=' },
            ],
          },
        },
        {
          name: 'locationSystem',
          label: t('general:locations.labels.system_workspace'),
          rules:
            vdata.titleEnd === 'System/Workspace' || vdata.titleEnd === 'workspace'
              ? 'required|string'
              : '',
          value:
            vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.workspace
              ? vdata.locationDetails.hierarchy.workspace.name
              : null,
          extra: {
            selectID:
              vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.workspace
                ? vdata.locationDetails.hierarchy.workspace.id
                : null,
            parentId:
              vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.room
                ? `/${vdata.locationDetails.company_id}/${
                    vdata.locationDetails.country_id
                  }?parent_id=${vdata.locationDetails.hierarchy.room.id}`
                : null,
            actionButton: true,
          },
        },
      ],
    },
    {
      // ADITIONAL INFORMATION
      name: 'locationContactDetails',
      label: t('general:locations.labels.details'),
      fields: [
        {
          name: 'phoneNumber',
          label: t('general:labels.contact_phone_number'),
          value: vdata && vdata.locationDetails ? vdata.locationDetails.contact_phone : null,
          rules: 'telephone',
        },
        {
          name: 'faxNumber',
          label: t('general:labels.contact_fax_number'),
          value: vdata && vdata.locationDetails ? vdata.locationDetails.contact_fax : null,
          rules: 'telephone',
        },
        {
          name: 'locationContact',
          label: t('general:locations.labels.contact'),
          value: vdata && vdata.locationDetails ? vdata.locationDetails.contact : null,
          rules: 'string',
        },
      ],
    },
    {
      name: 'locationAddressDetails',
      label: t('general:labels.address_details'),
      fields: [
        {
          name: 'streetNo',
          label: t('general:labels.street_number'),
          rules: vdata.titleEnd === 'Site' || vdata.titleEnd === 'site' ? 'required|string' : '',
          value:
            vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.site
              ? vdata.locationDetails.hierarchy.site.address.address_1
              : '',
        },
        {
          name: 'townCity',
          label: t('general:town_city'),
          rules: vdata.titleEnd === 'Site' || vdata.titleEnd === 'site' ? 'required|string' : '',
          value:
            vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.site
              ? vdata.locationDetails.hierarchy.site.address.address_2
              : '',
        },
        {
          name: 'stateCountryProvince',
          label: t('general:state_county_province'),
          rules: vdata.titleEnd === 'Site' || vdata.titleEnd === 'site' ? 'required|string' : '',
          value:
            vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.site
              ? vdata.locationDetails.hierarchy.site.address.address_4
              : '',
        },
        {
          name: 'zipCode',
          label: t('general:zip_postcode'),
          rules: vdata.titleEnd === 'Site' || vdata.titleEnd === 'site' ? 'required|string' : '',
          value:
            vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.site
              ? vdata.locationDetails.hierarchy.site.address.postcode
              : '',
        },
        {
          name: 'country',
          label: t('general:country'),
          rules: vdata.titleEnd === 'Site' || vdata.titleEnd === 'site' ? 'required|string' : '',
          value:
            vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.site
              ? vdata.locationDetails.hierarchy.site.country_name
              : '',
          extra: {
            selectID:
              vdata && vdata.locationDetails.hierarchy && vdata.locationDetails.hierarchy.site
                ? vdata.locationDetails.hierarchy.site.country_id
                : null,
            parentId: vdata && vdata.details ? vdata.details.id : null,
            actionButton: false,
          },
        },
      ],
    },
    {
      name: 'locationAssetDetails',
      label: t('general:assets.labels.assetised_location_details'),
      fields: [
        {
          name: 'assetizedLocation',
          label: t('general:assets.labels.assetised_location'),
          rules: 'boolean',
          value: Boolean(vdata && vdata.locationDetails && vdata.locationDetails.asset),
        },
        {
          name: 'assetType',
          label: t('general:labels.type'),
          value:
            vdata && vdata.locationDetails && vdata.locationDetails.asset
              ? vdata.locationDetails.asset.type
              : null,
          rules: vdata.locationDetails.asset ? 'required|string' : 'string',
          options: [
            { name: 'Rack' },
            { name: 'Bench' },
            { name: 'Trolley' },
            { name: 'Cabinet' },
            { name: 'Storage Cage' },
            { name: 'Work Cube' },
            { name: 'Desk' },
            { name: 'Van/Truck' },
          ],
        },
        {
          name: 'assetDescription',
          label: t('general:description'),
          value:
            vdata && vdata.locationDetails && vdata.locationDetails.asset
              ? vdata.locationDetails.asset.description
              : null,
          rules: vdata.locationDetails.asset ? 'required|string' : 'string',
        },
        {
          name: 'assetNumber',
          label: t('general:assets.labels.number'),
          value:
            vdata && vdata.locationDetails && vdata.locationDetails.asset
              ? vdata.locationDetails.asset.asset_number
              : null,
          rules: vdata.locationDetails.asset ? 'required|string' : 'string',
        },
        {
          name: 'assetBarcodeNumber',
          label: t('general:labels.barcode_number'),
          value:
            vdata && vdata.locationDetails && vdata.locationDetails.asset
              ? vdata.locationDetails.asset.barcode_number
              : null,
          rules: vdata.locationDetails.asset ? 'required|string' : 'string',
        },
        {
          name: 'assetSerialNumber',
          label: t('general:assets.labels.serial_number'),
          value:
            vdata && vdata.locationDetails && vdata.locationDetails.asset
              ? vdata.locationDetails.asset.er_serial_number
              : null,
          rules: vdata.locationDetails.asset ? 'required|string' : 'string',
        },
      ],
    },
    {
      // Location Type radio (customer/delivery/no-type)
      name: 'locationRequestFormType',
      label: t('general:locations.labels.type'),
      type: 'radio',
      rules: 'string|required',
      fields: [
        {
          name: 'list',
          value: radioInitialValue, // <-- initialize from rush first, else asset, else 'no-type'
          options: [
            { id: 'No Type', label: 'No Type', value: 'no-type' },
            { id: 'Customer', label: 'Customer', value: 'customer' },
            { id: 'Delivery', label: 'Delivery', value: 'delivery' },
          ],
        },
      ],
    },
  ];
};

class LocationFormEdit extends Form {}

/**
 * Submit hook:
 * - Reads radio value
 * - Decides MODE (rush/asset) preserving existing flags when possible
 * - Applies exclusive flags and persists
 */
export const locationEdit: Function = (
  fields: Array<*>,
  itemData: Object,
  localState: Object,
  accountStore: Object,
  t: Function
) => {
  const hooks: Object = {
    onSuccess(form: Object) {
      localState.isLoading = true;
      localState.responseMessage = t('general:messages.saving_form');
      localState.showResponseMessage = true;
      localState.hasError = false;
      localState.hasSuccess = true;

      const { tradingAccount }: Object = form.values().chooseAccount;

      const {
        countryId,
        locationCompany,
        locationSite,
        locationBuilding,
        locationRoom,
        locationSystem,
      }: Object = form.values().location;

      const {
        assetizedLocation,
        assetType,
        assetDescription,
        assetNumber,
        assetBarcodeNumber,
        assetSerialNumber,
      }: Object = form.values().locationAssetDetails;

      const {
        phoneNumber,
        faxNumber,
        locationContact,
      }: Object = form.values().locationContactDetails;

      const {
        streetNo,
        townCity,
        stateCountryProvince,
        zipCode,
      }: Object = form.values().locationAddressDetails;

      let locationName: string = '';
      switch (itemData.titleEnd) {
        case 'company':
        case 'Company':
          locationName = locationCompany;
          break;
        case 'site':
        case 'Site':
          locationName = locationSite;
          break;
        case 'building':
        case 'Building/Transport':
          locationName = locationBuilding;
          break;
        case 'room':
        case 'Lab/Room':
          locationName = locationRoom;
          break;
        case 'workspace':
        case 'System/Workspace':
          locationName = locationSystem;
          break;
      }

      // Read radio and choose mode to persist
      const typeValue: 'customer' | 'delivery' | 'no-type' = form.$('locationRequestFormType.list')
        .value;
      const mode: 'rush' | 'asset' | null = decideModeToPersist(itemData, accountStore);

      // Build base payload
      const locationData: any = {
        activity_tag: activities.edited.tag,
        name: locationName,
        country_id: form.$('locationAddressDetails.country').extra.selectID,
        company_id: form.$('location.locationCompany').extra.selectID,
        contact: locationContact,
        contact_phone: phoneNumber,
        contact_fax: faxNumber,
      };

      // Apply mutually-exclusive flags (rush vs asset)
      applyExclusiveFlags(locationData, mode, typeValue);

      // Assetized sub-entity
      if (assetizedLocation) {
        locationData.asset = {
          type: assetType,
          asset_number: assetNumber,
          barcode_number: assetBarcodeNumber,
          er_serial_number: assetSerialNumber,
          description: assetDescription,
        };
      } else if (itemData.assetType) {
        locationData.asset = null;
      }

      // Address (only when editing Site)
      if (itemData.titleEnd === 'Site' || itemData.titleEnd === 'site') {
        locationData.address = {
          postcode: zipCode,
          address_1: streetNo,
          address_2: townCity,
          address_4: stateCountryProvince,
        };
      }

      // === Company-level save ===
      const persistCompany = () =>
        apiService
          .putResource(`companies/${itemData.locationDetails.id}`, {
            activity_tag: activities.edited.tag,
            account_id: itemData.accountId,
            name: locationName,
            // Only flags (mutually exclusive)
            ...(function() {
              const obj = {};
              applyExclusiveFlags(obj, mode, typeValue);
              return obj;
            })(),
          })
          .then((response: Object) => {
            itemData.locationDetails.name = locationName;
            localState.isLoading = false;
            localState.responseMessage = t('general:messages.save_success');
            setTimeout(() => {
              localState.showResponseMessage = false;
              setTimeout(() => navigation.goBack(), 225);
            }, 1200);
            return response;
          })
          .catch((err: string) => {
            localState.isLoading = false;
            localState.responseMessage = t('general:messages.save_error');
            localState.hasSuccess = false;
            localState.hasError = true;
            console.log(err);
          });

      // === Location-level save ===
      const persistLocation = () =>
        apiService
          .putResource(`locations/${itemData.locationDetails.id}`, locationData)
          .then((response: Object) => {
            itemData.locationDetails.name = locationName;
            itemData.locationDetails.contact_phone = phoneNumber;
            itemData.locationDetails.contact_fax = faxNumber;
            itemData.locationDetails.contact = locationContact;

            if (assetizedLocation) {
              itemData.locationDetails.asset = {
                type: assetType,
                asset_number: assetNumber,
                barcode_number: assetBarcodeNumber,
                er_serial_number: assetSerialNumber,
                description: assetDescription,
              };
            } else {
              itemData.locationDetails.asset = null;
            }

            if (
              itemData.locationDetails.hierarchy &&
              itemData.locationDetails.hierarchy.site &&
              itemData.locationDetails.hierarchy.site.address
            ) {
              itemData.locationDetails.hierarchy.site.address.postcode = zipCode;
              itemData.locationDetails.hierarchy.site.address.address_1 = streetNo;
              itemData.locationDetails.hierarchy.site.address.address_2 = townCity;
              itemData.locationDetails.hierarchy.site.address.address_4 = stateCountryProvince;
              itemData.locationDetails.hierarchy.site.country_name = form.$(
                'locationAddressDetails.country'
              ).value;
              itemData.locationDetails.hierarchy.site.country_id = form.$(
                'locationAddressDetails.country'
              ).extra.selectID;
            }

            switch (itemData.titleEnd) {
              case 'Site':
                itemData.locationDetails.hierarchy.site.name = locationName;
                break;
              case 'Building/Transport':
                itemData.locationDetails.hierarchy.building.name = locationName;
                break;
              case 'Lab/Room':
                itemData.locationDetails.hierarchy.room.name = locationName;
                break;
              case 'System/Workspace':
                itemData.locationDetails.hierarchy.workspace.name = locationName;
                break;
            }

            const selectCountryID = form.$('locationAddressDetails.country').extra.selectID;
            if (itemData.locationDetails.country_id === selectCountryID) {
              accountStore.loadLocationDetails(itemData.locationDetails.id);
            } else {
              accountStore.updateLocations(accountStore.locationSelections[0], 0, false);
            }

            localState.isLoading = false;
            localState.responseMessage = t('general:messages.save_success');
            setTimeout(() => {
              localState.showResponseMessage = false;
              setTimeout(() => navigation.goBack(), 225);
            }, 1200);

            return response;
          })
          .catch((err: string) => {
            localState.isLoading = false;
            localState.responseMessage = t('general:messages.save_error');
            localState.hasSuccess = false;
            localState.hasError = true;
            console.log(err);
          });

      // Dispatch correct persist path
      if (itemData.titleEnd === 'Company' || itemData.titleEnd === 'company') {
        return persistCompany();
      }
      return persistLocation();
    },

    onError(form: Object) {
      localState.isLoading = false;
      localState.responseMessage = t('general:messages.form_error');
      localState.showResponseMessage = true;
      localState.hasSuccess = false;
      localState.hasError = true;
    },
  };

  return new LocationFormEdit({ fields }, { hooks, name: t('general:actions.edit_location') });
};
